import * as Sentry from '@sentry/nuxt';
import {ApiError, GsoaCustomerService} from '~/openapi';
import {useCustomerProfileStore} from '~/stores/customerProfile';

type FetchAuthStatusOptions = {
	force?: boolean,
};

const MAX_AUTH_STATUS_BODY_PREVIEW_LENGTH = 500;

export default function useAuth() {
	const isAuthenticated = useState<boolean>('auth_authenticated', () => false);
	const authStatusLoaded = useState<boolean>('auth_status_loaded', () => false);
	const customerProfileStore = useCustomerProfileStore();

	function clearLocalState() {
		isAuthenticated.value = false;
		authStatusLoaded.value = true;
		customerProfileStore.clear();
	}

	async function fetchAuthStatus(options: FetchAuthStatusOptions = {}) {
		if (authStatusLoaded.value && !options.force) {
			return;
		}

		try {
			const response = await cancelTimeout(() => GsoaCustomerService.getAuthStatus({}));

			if (response && response.authenticated) {
				isAuthenticated.value = true;
				authStatusLoaded.value = true;
				customerProfileStore.setProfile(response.profile || null);
			} else {
				clearLocalState();
			}
		} catch (e: any) {
			clearLocalState();
			// A failed SSR check (timeout, 5xx) says nothing about the session.
			// Leave the status unknown so the client re-checks after hydration.
			if (import.meta.server) {
				authStatusLoaded.value = false;
			}
			reportAuthStatusFailure(e);
		}
	};

	return {
		isAuthenticated,
		authStatusLoaded,
		fetchAuthStatus,
		clearLocalState,
	};
}

function reportAuthStatusFailure(error: unknown): void {
	Sentry.captureMessage('Failed to fetch auth status', {
		level: 'warning',
		tags: {area: 'auth-status'},
		contexts: {authStatus: getAuthStatusErrorContext(error)},
	});
}

function getAuthStatusErrorContext(error: unknown): Record<string, unknown> {
	if (error instanceof ApiError) {
		return {
			status: error.status,
			statusText: error.statusText,
			url: error.url,
			bodyType: typeof error.body,
			bodyPreview: getErrorBodyPreview(error.body),
		};
	}

	if (error instanceof Error) {
		return {
			name: error.name,
			message: error.message,
		};
	}

	return {
		type: typeof error,
		value: String(error),
	};
}

function getErrorBodyPreview(body: unknown): string | undefined {
	if (body === null || body === undefined) {
		return undefined;
	}

	try {
		const bodyPreview = typeof body === 'string' ? body : JSON.stringify(body);

		return bodyPreview.slice(0, MAX_AUTH_STATUS_BODY_PREVIEW_LENGTH);
	} catch {
		return '[unserializable body]';
	}
}
